import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../App';
import { toast } from 'react-toastify';
import MatchRegistrationModal from './MatchRegistrationModal';
import MatchDetailsModal from './MatchDetailsModal';
import { getMyRegistrations } from '../../api/registrationsApi';
import './MatchList.css';

function MatchList({ session }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('scheduled');
  const [modeFilter, setModeFilter] = useState('all');

  const [selectedMatch, setSelectedMatch] = useState(null);
  const [viewMatch, setViewMatch] = useState(null);
  const [registeredMatchIds, setRegisteredMatchIds] = useState(new Set());

  useEffect(() => {
    fetchMatches();
    if (session?.access_token) {
      fetchMyRegistrations();
    }
  }, [filter, modeFilter, session]);

  const fetchMyRegistrations = async () => {
    try {
      const regs = await getMyRegistrations(session.access_token);
      const ids = new Set(regs.map(r => r.match_id));
      setRegisteredMatchIds(ids);
    } catch (err) {
      console.error('Failed to fetch user registrations', err);
    }
  };

  const fetchMatches = async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/matches?status=${filter}`;
      if (modeFilter !== 'all') {
        url += `&mode=${modeFilter}`;
      }

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setMatches(data);
      }
    } catch (error) {
      console.error('Error fetching matches:', error);
      toast.error('Failed to load matches');
    } finally {
      setLoading(false);
    }
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'solo': return '👤';
      case 'duo': return '👥';
      case 'squad': return '👨‍👩‍👦‍👦';
      default: return '🎮';
    }
  };

  const getStatusBadge = (match) => {
    const startTime = new Date(match.start_ts);
    const now = new Date();

    if (startTime < now) {
      return <span className="badge badge-danger">Ended</span>;
    } else if (startTime - now < 3600000) {
      return <span className="badge badge-warning">Starting Soon</span>;
    } else {
      return <span className="badge badge-success">Upcoming</span>;
    }
  };

  const getSlotsAvailable = (match) => {
    const taken = match.registrations_count || 0;
    const available = match.slot_count - taken;
    return available;
  };


  const getPerPlayerCost = (match) => {
    let teamSize = 1;
    if (match.mode === 'duo') teamSize = 2;
    if (match.mode === 'squad') teamSize = 4;
    return match.entry_fee / teamSize;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
        <p style={{ marginTop: '20px', color: 'var(--text-secondary)' }}>Loading matches...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{
          fontSize: '2rem',
          marginBottom: '20px',
          fontFamily: 'Orbitron, sans-serif',
          color: 'var(--primary-color)'
        }}>
          🎮 Available Matches
        </h2>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <button
            className={`btn ${filter === 'upcoming' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setFilter('upcoming')}
          >
            Upcoming
          </button>
          <button
            className={`btn ${filter === 'completed' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setFilter('completed')}
          >
            Completed
          </button>

          <div style={{ marginLeft: 'auto' }}>
            {/* Desktop View - Buttons */}
            <div className="mode-filter-desktop">
              <button
                className={`btn ${modeFilter === 'all' ? 'btn-secondary' : 'btn-outline'}`}
                onClick={() => setModeFilter('all')}
              >
                All Modes
              </button>
              <button
                className={`btn ${modeFilter === 'solo' ? 'btn-secondary' : 'btn-outline'}`}
                onClick={() => setModeFilter('solo')}
              >
                Solo
              </button>
              <button
                className={`btn ${modeFilter === 'duo' ? 'btn-secondary' : 'btn-outline'}`}
                onClick={() => setModeFilter('duo')}
              >
                Duo
              </button>
              <button
                className={`btn ${modeFilter === 'squad' ? 'btn-secondary' : 'btn-outline'}`}
                onClick={() => setModeFilter('squad')}
              >
                Squad
              </button>
            </div>

            {/* Mobile View - Dropdown */}
            <select
              className="mode-filter-mobile"
              value={modeFilter}
              onChange={(e) => setModeFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="solo">Solo</option>
              <option value="duo">Duo</option>
              <option value="squad">Squad</option>
            </select>
          </div>
        </div>
      </div>

      {/* Matches Grid */}
      {matches.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: 'var(--text-secondary)'
        }}>
          <p style={{ fontSize: '4rem', marginBottom: '20px' }}>🎯</p>
          <h3 style={{ fontSize: '1.5rem', marginBottom: '10px' }}>No matches found</h3>
          <p>Check back later for upcoming tournaments!</p>
        </div>
      ) : (
        <div className="grid grid-2">
          {matches.map((match) => (
            <div key={match.id} className="match-card">
              <div className="match-header">
                <div>
                  <h3 className="match-title">{match.title}</h3>
                  {getStatusBadge(match)}
                </div>
                <div style={{ fontSize: '2rem' }}>
                  {getModeIcon(match.mode)}
                </div>
              </div>

              <div className="match-info">
                <div className="match-info-item">
                  <span>🗺️</span>
                  <span>{match.map}</span>
                </div>
                <div className="match-info-item">
                  <span>🎯</span>
                  <span style={{ textTransform: 'uppercase' }}>{match.mode}</span>
                </div>
                <div className="match-info-item">
                  <span>💰</span>
                  <span>{match.prize_pool_bigint ? `Prize: ₹${match.prize_pool_bigint / 100}` : `Fee: ₹${match.entry_fee}`}</span>
                </div>
                {/* Check if per_kill exists and is > 0 */}
                {match.per_kill > 0 && (
                  <div className="match-info-item">
                    <span>⚔️</span>
                    <span>Per Kill: <span style={{ color: '#ff4757', fontWeight: 'bold' }}>₹{match.per_kill}</span></span>
                  </div>
                )}
                {/* Show Per Player cost for teams */}
                {match.mode !== 'solo' && match.entry_fee > 0 && (
                  <div className="match-info-item">
                    <span>👤</span>
                    <span>Per Player: ₹{getPerPlayerCost(match)}</span>
                  </div>
                )}
                <div className="match-info-item">
                  <span>📅</span>
                  <span>{new Date(match.start_ts).toLocaleDateString()}</span>
                </div>
                <div className="match-info-item">
                  <span>⏰</span>
                  <span>{new Date(match.start_ts).toLocaleTimeString()}</span>
                </div>
              </div>

              <div className="match-footer">
                <div>
                  <div style={{
                    color: 'var(--text-secondary)',
                    fontSize: '0.85rem',
                    marginBottom: '4px'
                  }}>
                    Available Slots
                  </div>
                  <div style={{
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    color: getSlotsAvailable(match) > 10 ? 'var(--success-color)' : 'var(--warning-color)'
                  }}>
                    {getSlotsAvailable(match)} / {match.slot_count}
                  </div>
                </div>
                <button
                  className="btn btn-outline"
                  style={{ marginRight: '8px' }}
                  onClick={() => setViewMatch(match)}
                >
                  View Details
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => setSelectedMatch(match)}
                  disabled={
                    getSlotsAvailable(match) === 0 ||
                    new Date(match.start_ts) < new Date() ||
                    registeredMatchIds.has(match.id)
                  }
                >
                  {registeredMatchIds.has(match.id)
                    ? 'Registered'
                    : getSlotsAvailable(match) === 0 ? 'Full' : 'Register'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Registration Modal */}
      {selectedMatch && (
        <MatchRegistrationModal
          match={selectedMatch}
          session={session}
          onClose={() => setSelectedMatch(null)}
          onSuccess={() => {
            setSelectedMatch(null);
            fetchMatches();
            fetchMyRegistrations(); // Refresh registration status
            toast.success('Successfully registered for match! 🎮');
          }}
        />
      )}

      {/* Match Details Modal */}
      {viewMatch && (
        <MatchDetailsModal
          match={viewMatch}
          isRegistered={registeredMatchIds.has(viewMatch.id)}
          onClose={() => setViewMatch(null)}
          onRegister={() => {
            // If allow registering from details view
            setViewMatch(null);
            setSelectedMatch(viewMatch);
          }}
        />
      )}
    </div>
  );
}

export default MatchList;
